import React, { useEffect, useState } from "react";
import { Input, Select } from "antd";
import toast from "react-hot-toast";
import Segmented from "../../components/segmented/Segmented.jsx";
import MT5AccountsPending from "./MT5AccountsPending.jsx";
import MT5AccountAccepted from "./MT5AccountAccepted.jsx";
import MT5AccountReject from "./MT5AccountReject.jsx";
import { GetAllAccountApi } from "../../api/request/account.js";
import ExportHandler from "../../components/global/export/ExportHandler.jsx";
import { GetAllAccountTypeApi } from "../../api/request/ibapproved.js";
import LoadableButton from "../../components/buttons/LoadableButton.jsx";
import { UpdateAccountTypeApi } from "../../api/request/accountType.js";
import ModalComponent from "../../components/global/modal/ModalComponent.jsx";
import columns from "../../columns/MT5Account/UpdateAcTypeError.js";
import TableWithSkeleton from "../../components/global/table/TableWithSkeleton.jsx";
import UserAvatar from "../../components/global/avtar/UserAvatar.jsx";

export default function Accounts() {
  const [search, setSearch] = useState("");
  const [searchText, setSearchText] = useState("");
  const [value, setValue] = useState("Pending");
  const [accountTypes, setAccountTypes] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedAccountType, setSelectedAccountType] = useState(null);
  const [isUpdateLoading, setIsUpdateLoading] = useState(false);
  const [isRefresh, setIsRefresh] = useState(false);
  const [updateError, setUpdateError] = useState([]);
  const [isOpenModal, setIsOpenModal] = useState(false);

  const getAllAccountTypes = async () => {
    try {
      const params = { AccountType: true };
      const { data } = await GetAllAccountTypeApi(
        new URLSearchParams(params).toString()
      );
      if (data?.success) {
        const options = data?.data?.map((item) => ({
          label: item?.planName,
          value: item?.planId,
        }));
        setAccountTypes(options);
      }
    } catch (error) {}
  };

  useEffect(() => {
    getAllAccountTypes();
  }, []);

  const handleUpdateAccountType = async () => {
    if (!selectedAccountType || !selectedRowKeys?.length) return;

    setIsUpdateLoading(true);
    try {
      const params = {
        loginIds: selectedRowKeys,
        accountTypeId: selectedAccountType,
      };
      const { data } = await UpdateAccountTypeApi(params);
      if (data?.success) {
        toast.success(data?.message);
        setSelectedRowKeys([]);
        setSelectedAccountType(null);
        setIsRefresh(!isRefresh);
        if (data?.data?.length) {
          setUpdateError(
            data?.data?.map((item) => ({
              ...item,
              userDetails: (
                <UserAvatar
                  data={{
                    name: item?.name,
                    email: item?.email,
                    profileImage: item?.profileImage,
                  }}
                />
              ),
            }))
          );
          setIsOpenModal(true);
        }
      }
    } catch (error) {
      toast.error(error?.response?.data?.error?.message || "An error occurred");
    } finally {
      setIsUpdateLoading(false);
    }
  };

  const exportHandler = async () => {
    try {
      let params = {
        ApplyPagination: false,
        SearchText: searchText,
        FilterText:
          value === "Pending"
            ? "Pending"
            : value === "Approved"
            ? "Approved"
            : value === "Declined"
            ? "Rejected"
            : value,
        SortBy: "createdDate",
      };
      const { data } = await GetAllAccountApi(
        new URLSearchParams(params).toString()
      );
      if (data?.success) {
        return {
          data: data?.data,
          removableColumns: ["userId", "mT5Id"],
          fileName: "Accounts",
        };
      } else {
        return { data: [], removableColumns: [] };
      }
    } catch (error) {
      return { data: [], removableColumns: [] };
    }
  };

  return (
    <div>
      <div className="flex justify-between flex-wrap gap-5 items-center">
        <Segmented
          options={["Pending", "Approved", "Declined"]}
          value={value}
          setValue={setValue}
        />

        <div className="flex items-center gap-3">
          {value === "Approved" && selectedRowKeys?.length ? (
            <div className="flex items-center gap-3">
              <Select
                className="ant-select-selector-white"
                placeholder="Select account type"
                options={accountTypes}
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                value={selectedAccountType}
                onChange={(value) => setSelectedAccountType(value)}
              />

              <LoadableButton
                className="bg-primary text-sm text-white font-semibold px-4 py-2 rounded-lg"
                lable="Update"
                loadingLable="updating..."
                isLoading={isUpdateLoading}
                onClick={handleUpdateAccountType}
                disabled={isUpdateLoading || !selectedAccountType}
              />
            </div>
          ) : null}

          <div>
            <Input
              size="large"
              style={{ width: 220 }}
              placeholder="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onPressEnter={() => setSearchText(search)}
              prefix={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  class="w-5 h-5"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10.5 3.75a6.75 6.75 0 1 0 0 13.5 6.75 6.75 0 0 0 0-13.5ZM2.25 10.5a8.25 8.25 0 1 1 14.59 5.28l4.69 4.69a.75.75 0 1 1-1.06 1.06l-4.69-4.69A8.25 8.25 0 0 1 2.25 10.5Z"
                    clip-rule="evenodd"
                  />
                </svg>
              }
              suffix={
                search && (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    onClick={() => {
                      setSearchText("");
                      setSearch("");
                    }}
                    viewBox="0 0 24 24"
                    fill="#bebebe"
                    className="size-5 cursor-pointer duration-300 hover:fill-[#969595]"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25Zm-1.72 6.97a.75.75 0 1 0-1.06 1.06L10.94 12l-1.72 1.72a.75.75 0 1 0 1.06 1.06L12 13.06l1.72 1.72a.75.75 0 1 0 1.06-1.06L13.06 12l1.72-1.72a.75.75 0 1 0-1.06-1.06L12 10.94l-1.72-1.72Z"
                      clip-rule="evenodd"
                    />
                  </svg>
                )
              }
            />
          </div>

          <div>
            <ExportHandler exportHandler={exportHandler} />
          </div>
        </div>
      </div>

      {value === "Pending" ? <MT5AccountsPending dates={searchText} /> : null}

      {value === "Approved" ? (
        <MT5AccountAccepted
          dates={searchText}
          selectedRowKeys={selectedRowKeys}
          setSelectedRowKeys={setSelectedRowKeys}
          isRefresh={isRefresh}
        />
      ) : null}

      {value === "Declined" ? <MT5AccountReject dates={searchText} /> : null}

      <ModalComponent
        isOpen={isOpenModal}
        setIsOpen={setIsOpenModal}
        title="Update Account Type Error"
        width={1200}
      >
        <TableWithSkeleton columns={columns} data={updateError} />
      </ModalComponent>
    </div>
  );
}
